"use client";

import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Shared container for entity-item lists (jobs, contacts, companies).
 *
 *   mode="separated" — items are standalone cards, gapped by space-y-3.
 *                      Use for flat feeds (job search, contacts library,
 *                      reports list).
 *   mode="grouped"   — items share one bordered wrapper with 2px dividers.
 *                      Use inside a bucket header (ContactGroupBlocks
 *                      executives/decision-makers, brief branch-out).
 *
 * Caller is responsible for rendering entity items via its own preset
 * (JobEntity.Feed, ContactEntity, etc.). EntityList only owns the wrapper,
 * spacing, header slot, empty/loading/"show more" state.
 */

type EntityListMode = "separated" | "grouped";

export interface EntityListProps<T> {
  items: readonly T[];
  renderItem: (item: T, index: number) => React.ReactNode;

  /** Rendered above the list. Typically a <SectionPanel />. */
  header?: React.ReactNode;

  mode?: EntityListMode;

  /** Renders instead of the list when items.length === 0 and not loading. */
  emptyState?: React.ReactNode;

  /** When true, skeletons render instead of items. */
  loading?: boolean;
  skeleton?: (index: number) => React.ReactNode;
  skeletonCount?: number;

  /** Progressive disclosure: show initialVisibleCount, grow by visibleStep. */
  progressiveDisclosure?: boolean;
  initialVisibleCount?: number;
  visibleStep?: number;
  showMoreLabel?: string;
  showLessLabel?: string;

  /** Key extractor. Defaults to `(item as { id }).id` or index fallback. */
  itemKey?: (item: T, index: number) => React.Key;

  className?: string;
  listClassName?: string;
}

function defaultKey<T>(item: T, index: number): React.Key {
  if (item && typeof item === "object" && "id" in item) {
    const id = (item as { id?: unknown }).id;
    if (typeof id === "string" || typeof id === "number") return id;
  }
  return index;
}

function EntityListBase<T>({
  items,
  renderItem,
  header,
  mode = "separated",
  emptyState,
  loading = false,
  skeleton,
  skeletonCount = 3,
  progressiveDisclosure = false,
  initialVisibleCount = 5,
  visibleStep = 5,
  showMoreLabel = "Show more",
  showLessLabel = "Show less",
  itemKey = defaultKey,
  className,
  listClassName,
}: EntityListProps<T>) {
  const [visibleCount, setVisibleCount] = React.useState(() =>
    progressiveDisclosure
      ? Math.min(items.length, Math.max(1, initialVisibleCount))
      : items.length,
  );

  React.useEffect(() => {
    if (!progressiveDisclosure) return;
    setVisibleCount((current) => {
      const capped = Math.min(current, items.length);
      return capped === current ? current : capped;
    });
  }, [items.length, progressiveDisclosure]);

  const resolvedVisible = progressiveDisclosure
    ? Math.min(items.length, Math.max(1, visibleCount))
    : items.length;

  const visibleItems = progressiveDisclosure
    ? items.slice(0, resolvedVisible)
    : items;

  const hasMore =
    progressiveDisclosure && resolvedVisible < items.length;
  const canShowLess =
    progressiveDisclosure && resolvedVisible > Math.max(1, initialVisibleCount);

  const handleShowMore = () => {
    setVisibleCount((current) =>
      Math.min(items.length, current + Math.max(1, visibleStep)),
    );
  };

  const handleShowLess = () => {
    setVisibleCount(Math.max(1, initialVisibleCount));
  };

  const itemsNode = (() => {
    if (loading && skeleton) {
      const indexes = Array.from({ length: Math.max(1, skeletonCount) });
      return indexes.map((_, i) => (
        <React.Fragment key={`skeleton-${i}`}>{skeleton(i)}</React.Fragment>
      ));
    }
    if (items.length === 0) {
      return emptyState ?? null;
    }
    return visibleItems.map((item, index) => (
      <React.Fragment key={itemKey(item, index)}>
        {renderItem(item, index)}
      </React.Fragment>
    ));
  })();

  const wrapperClasses =
    mode === "grouped"
      ? "border border-border bg-card divide-y divide-border"
      : "flex flex-col gap-3";

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {header}
      <div className={cn(wrapperClasses, listClassName)}>{itemsNode}</div>
      {(hasMore || canShowLess) && items.length > 0 && !loading ? (
        <div className="flex items-center justify-center gap-2">
          {canShowLess ? (
            <button
              type="button"
              onClick={handleShowLess}
              className="h-7 px-2.5 border border-border bg-card text-overline text-foreground hover:bg-muted/50 transition-colors"
            >
              {showLessLabel}
            </button>
          ) : null}
          {hasMore ? (
            <button
              type="button"
              onClick={handleShowMore}
              className="h-7 px-2.5 border border-border bg-card text-overline text-foreground hover:bg-muted/50 transition-colors"
            >
              {showMoreLabel}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------ EntityListEmpty ------------------------ */

export interface EntityListEmptyProps {
  icon?: LucideIcon;
  title: React.ReactNode;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

function EntityListEmpty({
  icon: Icon,
  title,
  hint,
  action,
  className,
}: EntityListEmptyProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 border border-dashed border-border bg-background px-4 py-8 text-center",
        className,
      )}
    >
      {Icon ? <Icon className="h-5 w-5 text-muted-foreground" /> : null}
      <p className="text-body-sm text-foreground">{title}</p>
      {hint ? (
        <p className="text-caption text-muted-foreground max-w-sm">{hint}</p>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

/* ------------------------------ exports -------------------------------- */

type EntityListComponent = {
  <T>(props: EntityListProps<T>): React.ReactElement | null;
  Empty: typeof EntityListEmpty;
};

export const EntityList = EntityListBase as EntityListComponent;
EntityList.Empty = EntityListEmpty;
